import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import ModalShell from '../../ui/ModalShell';
import Dropdown from '../../receptura/Dropdown';
import { SECTION_COLOR, SECTION_BG, hlavicky } from '../komunikace';
import { DRUHY_UKOLU } from './ukolyUi';
import PrilohyVyber from '../prilohy/PrilohyVyber';

/**
 * Nový úkol nebo hlášení — `ModalShell`.
 *
 * - `rezim="ukol"`: úkol konkrétnímu člověku (typicky ze zprávy — „Udělat úkol",
 *   text zprávy je předvyplněný, komu = člen konverzace), termín.
 * - `rezim="hlaseni"`: závada, vozidlo, surovina, reklamace, jiné — s fotkou
 *   (na telefonu rovnou fotoaparát), naléhavé; jde skupině podle druhu.
 *
 * Fotky a soubory jdou přiložit k obojímu (`PrilohyVyber`) — vyfotit, poslat
 * a vysvětlit je dnes nejrychlejší způsob, jak zadat práci i nahlásit problém.
 */
const HLASENI = ['zavada', 'vozidlo', 'surovina', 'reklamace', 'jine'];

export default function NovyUkol({ rezim = 'hlaseni', isDark, lide = [], konverzaceId = null, zdrojZprava = null, onClose, onHotovo }) {
    const [druh, setDruh] = useState(rezim === 'ukol' ? 'ukol' : null);
    const [nazev, setNazev] = useState(zdrojZprava?.obsah ? zdrojZprava.obsah.slice(0, 200) : '');
    const [popis, setPopis] = useState('');
    const [resitel, setResitel] = useState(null);
    const [termin, setTermin] = useState('');
    const [nalehave, setNalehave] = useState(false);
    const [vozidlo, setVozidlo] = useState(null);
    const [vozidla, setVozidla] = useState([]);
    const [prilohy, setPrilohy] = useState([]);
    const [nahravam, setNahravam] = useState(false);
    const [ukladam, setUkladam] = useState(false);
    const [chyba, setChyba] = useState(null);

    useEffect(() => {
        if (druh !== 'vozidlo' || vozidla.length) return;
        fetch('/api/ukoly').then(r => (r.ok ? r.json() : null)).then(d => setVozidla(d?.vozidla || [])).catch(() => {});
    }, [druh, vozidla.length]);

    const terminIso = () => {
        if (!termin) return null;
        const d = new Date();
        if (termin === 'dnes') { d.setHours(23, 59, 0, 0); return d.toISOString(); }
        if (termin === 'zitra') { d.setDate(d.getDate() + 1); d.setHours(23, 59, 0, 0); return d.toISOString(); }
        return termin;
    };

    const lze = druh && nazev.trim() && (druh !== 'ukol' || resitel) && !ukladam && !nahravam;

    const ulozit = async () => {
        if (!lze) return;
        setUkladam(true);
        setChyba(null);
        try {
            const res = await fetch('/api/ukoly', {
                method: 'POST', headers: hlavicky(),
                body: JSON.stringify({
                    druh, nazev: nazev.trim(), popis: popis.trim() || null,
                    resitel_id: druh === 'ukol' ? resitel : null,
                    termin: terminIso(), nalehave, vozidlo_id: druh === 'vozidlo' ? vozidlo : null,
                    prilohy: prilohy.map(p => p.id), konverzace_id: konverzaceId, zdroj_zprava_id: zdrojZprava?.id ?? null,
                }),
            });
            const d = await res.json().catch(() => ({}));
            if (!res.ok) { setChyba(d.error || (d.errors ? Object.values(d.errors)[0]?.[0] : null) || 'Nepodařilo se uložit.'); return; }
            onHotovo(d);
        } catch {
            setChyba('Chyba sítě — zkuste to znovu.');
        } finally {
            setUkladam(false);
        }
    };

    const hrana = isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)';
    const pole = { width: '100%', height: 42, borderRadius: 8, padding: '0 12px', outline: 'none', fontSize: 15, background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)', border: `1px solid ${hrana}`, color: 'var(--text-primary)', colorScheme: isDark ? 'dark' : 'light' };
    const popisek = { fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-muted)', margin: '14px 0 6px' };
    const volba = (on) => ({ height: 38, padding: '0 12px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', background: on ? SECTION_BG : 'transparent', border: `1px solid ${on ? SECTION_COLOR : hrana}`, color: on ? SECTION_COLOR : 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: 6 });

    const Info = druh ? DRUHY_UKOLU[druh] : null;

    return (
        <ModalShell onClose={onClose} isDark={isDark} nadpis={rezim === 'ukol' ? 'Udělat úkol' : druh ? `Hlášení: ${Info.nazev}` : 'Nové hlášení'}
            ikona={Info?.Icon || DRUHY_UKOLU.zavada.Icon} barva={SECTION_COLOR} sirka={500}
            paticka={druh ? (
                <div className="flex items-center gap-2">
                    {chyba && <span className="text-xs flex-1" style={{ color: '#EF4444' }}>{chyba}</span>}
                    <button type="button" onClick={ulozit} disabled={!lze} className="rounded-lg font-semibold ml-auto"
                        style={{ height: 44, padding: '0 20px', background: SECTION_COLOR, color: '#fff', border: 'none', cursor: lze ? 'pointer' : 'not-allowed', opacity: lze ? 1 : 0.45 }}>
                        {ukladam ? 'Ukládám…' : druh === 'ukol' ? 'Zadat úkol' : 'Nahlásit'}
                    </button>
                </div>
            ) : null}>
            <div style={{ padding: '0 16px 16px' }}>
                {rezim !== 'ukol' && !druh && (
                    <div className="flex flex-col" style={{ gap: 8, marginTop: 12 }}>
                        {HLASENI.map(d => {
                            const { nazev: n, Icon, komu } = DRUHY_UKOLU[d];
                            return (
                                <button key={d} type="button" onClick={() => setDruh(d)} className="flex items-center text-left"
                                    style={{ gap: 12, minHeight: 56, padding: '8px 12px', borderRadius: 10, border: `1px solid ${hrana}`, background: 'transparent', cursor: 'pointer' }}>
                                    <span style={{ width: 36, height: 36, borderRadius: 9, background: SECTION_BG, color: SECTION_COLOR, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon size={18} /></span>
                                    <span>
                                        <span style={{ display: 'block', fontWeight: 700, fontSize: 14.5, color: 'var(--text-primary)' }}>{n}</span>
                                        <span style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)' }}>Dostane: {komu}</span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                )}

                {druh && (
                    <>
                        <p style={popisek}>{druh === 'ukol' ? 'Co udělat' : 'Co se děje'}</p>
                        <input value={nazev} onChange={e => setNazev(e.target.value)} maxLength={200} style={pole} autoFocus={!nazev}
                            placeholder={{ ukol: 'Např. dotankovat Fabii', zavada: 'Např. teče myčka', vozidlo: 'Např. svítí kontrolka motoru', surovina: 'Např. došla mozzarella', reklamace: 'Např. studená pizza, obj. #38412', jine: 'Stručně, o co jde' }[druh]} />

                        {druh === 'ukol' && (
                            <>
                                <p style={popisek}>Komu</p>
                                <Dropdown value={resitel} onChange={setResitel} isDark={isDark} accent={SECTION_COLOR} height={42}
                                    placeholder="Vyberte kolegu" options={lide.map(l => ({ value: l.id, label: l.jmeno }))} />
                            </>
                        )}

                        {druh === 'vozidlo' && (
                            <>
                                <p style={popisek}>Které auto</p>
                                <Dropdown value={vozidlo} onChange={setVozidlo} isDark={isDark} accent={SECTION_COLOR} height={42}
                                    placeholder="Vyberte vozidlo" options={vozidla.map(v => ({ value: v.id, label: v.nazev }))} />
                            </>
                        )}

                        <p style={popisek}>{druh === 'ukol' ? 'Do kdy' : 'Termín (nepovinné)'}</p>
                        <div className="flex flex-wrap gap-2 items-center">
                            <button type="button" style={volba(termin === '')} onClick={() => setTermin('')}>Bez termínu</button>
                            <button type="button" style={volba(termin === 'dnes')} onClick={() => setTermin('dnes')}>Dnes</button>
                            <button type="button" style={volba(termin === 'zitra')} onClick={() => setTermin('zitra')}>Zítra</button>
                            <input type="date" value={['', 'dnes', 'zitra'].includes(termin) ? '' : termin} onChange={e => setTermin(e.target.value)} style={{ ...pole, width: 170, height: 38 }} />
                        </div>

                        <p style={popisek}>Podrobnosti (nepovinné)</p>
                        <textarea value={popis} onChange={e => setPopis(e.target.value)} rows={3} style={{ ...pole, height: 'auto', padding: '10px 12px' }} />

                        <PrilohyVyber typ="ukol" prilohy={prilohy} onChange={setPrilohy} onNahravani={setNahravam} isDark={isDark} />

                        {druh !== 'ukol' && (
                            <>
                                <button type="button" onClick={() => setNalehave(v => !v)} className="flex items-center gap-2" style={{ marginTop: 14, background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-primary)', fontSize: 14 }}>
                                    <span style={{ width: 20, height: 20, borderRadius: 5, border: `1.5px solid ${nalehave ? '#EF4444' : hrana}`, background: nalehave ? '#EF4444' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{nalehave && <Check size={14} color="#fff" />}</span>
                                    Naléhavé — brání v práci
                                </button>
                                <p className="text-xs" style={{ color: 'var(--text-muted)', marginTop: 10 }}>Dostane: {Info.komu}.</p>
                            </>
                        )}
                    </>
                )}
            </div>
        </ModalShell>
    );
}
